import React, { useContext, useState } from 'react';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@material-ui/core';
import { Delete, Edit, Visibility } from '@material-ui/icons';

import PropTypes from 'prop-types';

import CustomCheckBox from '../../fields/customCheckBox';
import DeleteToolBar from '../../fields/deleteToolBar';
import TablePaginationActions from '../../fields/tablePaginationActions';

import { getComparator, stableSort, usersWithAccessList } from '../../../utils/helpers';
import { defaultRowsPerPage, defaultSortOrder, rowsPerPageOptions } from '../../../utils/constants';

import translations from '../../../languages/i18n';

import fieldStyles from '../../../styles/fieldStyles';
import { customOptionsTableStyles, tableTitle } from '../../../styles/formElementsStyles';

import { SessionContext } from '../../../utils/context';

export default function ClinicianProgramsList({ administeredPrograms, handleEditProgram, handleRemove, handleSelectedProgram, programsData, selectedPrograms }) {
  const classesField = fieldStyles();
  const bgColors = customOptionsTableStyles();
  const classesTable = tableTitle();

  const sessionContext = useContext(SessionContext);

  const [page, setPage] = useState(0);
  const [order, setOrder] = React.useState(defaultSortOrder); // Indica l'ordre per defecte
  const [orderBy, setOrderBy] = React.useState('name'); // Indica la columna per la que ordenam per defecte
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCheck = (id, descriptiveName) => {
    handleSelectedProgram(id, descriptiveName);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Decidim si es pot eliminar el clínic del programa o no
  const removableProgram = (programId) => {
    // Si l'usuari que consulta la interfície és l'administrador del sistema pot eliminar qualsevol clínic
    if (sessionContext.role === 'Admin') return true;
    // Si no és l'administrador del sistema, el clínic només pot eliminar els clínics dels programes que administra
    if (administeredPrograms.some((progId) => programId === progId)) return true;
    // En qualsevol altre cas no es pot eliminar el clínic
    return false;
  }


  const programsDataColumns = 5; // Per saber el nombre de propietats de cada objecte

  return (
    <TableContainer component={Paper}>
      <Table aria-label='programs list table'>
        <TableHead>
          <TableRow>
            <TableCell colSpan={programsDataColumns + 1} align='left' padding='none'>
              {(selectedPrograms.length > 0 ? (
                <DeleteToolBar numSelected={selectedPrograms.length} handleClick={handleRemove} />
              ) : (
                <Typography variant='h5' component='div' className={classesTable.title}>{translations['_cliniciansActivePrograms']}</Typography>
              ))}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow className={bgColors.tableRowHeaderBgColor}>
            {/* ATENCIÓ: no pintam la columna _id */}
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'name'}
                direction={orderBy === 'name' ? order : 'asc'}
                onClick={() => handleRequestSort('name')}
              >
                <strong>{translations['_name']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'sharedId'}
                direction={orderBy === 'sharedId' ? order : 'asc'}
                onClick={() => handleRequestSort('sharedId')}
              >
                <strong>{translations['_sharedId']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='left'>
              <TableSortLabel
                active={orderBy === 'type'}
                direction={orderBy === 'type' ? order : 'asc'}
                onClick={() => handleRequestSort('type')}
              >
                <strong>{translations['_type']}</strong>
              </TableSortLabel>
            </TableCell>
            <TableCell align='left'><strong>{translations['_accessibleBy']}</strong></TableCell>
            <TableCell align='center' />{/* Columna per al botó d'editar */}
            <TableCell align='center'><Delete className={classesField.iconBody} /></TableCell>
          </TableRow>
          {(rowsPerPage > 0
            // ATENCIÓ: l'ordenació es fa damunt programsData, si no s'ha introduit res al camp de cerca el programsData inclou tot els programes,
            // és a dir, l'ordenació no es fa sobre la "pagina de programes" que es mostra al llistat (estigui filtrada o no)
            ? stableSort(programsData, getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            : stableSort(programsData, getComparator(order, orderBy))
          ).map((data, index) => (
            <TableRow key={data._id} className={index % 2 ? bgColors.tableRowBgColor1 : bgColors.tableRowBgColor2} hover>
              {/* ATENCIÓ: no pintam la columna _id */}
              <TableCell align='left'>{translations[data.sharedId] || data.name}</TableCell>
              <TableCell align='left'>{data.sharedId}</TableCell>
              <TableCell align='left'>{data.type}</TableCell>
              <TableCell align='left'>{data.accessibleBy.clinicians && usersWithAccessList(data.accessibleBy.clinicians, true)}</TableCell>
              <TableCell align='center'>
                <IconButton onClick={() => handleEditProgram(data._id)}>
                  {sessionContext.role === 'Admin' ? <Edit /> : <Visibility />}
                </IconButton>
              </TableCell>
              <TableCell align='center'>
                <CustomCheckBox
                  checked={selectedPrograms.findIndex((item) => item.id === data._id) > -1} // Si res.value es dins la taula la condició dona true
                  onClick={() => handleCheck(data._id, data.name)}
                  disabled={!removableProgram(data._id)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={programsDataColumns + 1}
              count={programsData.length}
              rowsPerPageOptions={rowsPerPageOptions}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { 'aria-label': 'rows per page' },
                native: true,
              }}
              labelDisplayedRows={({ from, to, count }) => from + ' - ' + ((to !== -1 && to) || count) + ' ' + translations['_of'] + ' ' + ((count !== -1 && count) || (translations['_moreThan'] + ' ') + to)}
              labelRowsPerPage={translations['_rowsPerPage']}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

// Indica i controla els props que se han de passar al component
ClinicianProgramsList.propTypes = {
  administeredPrograms: PropTypes.array.isRequired,
  handleEditProgram: PropTypes.func.isRequired,
  handleRemove: PropTypes.func.isRequired,
  handleSelectedProgram: PropTypes.func.isRequired,
  programsData: PropTypes.array.isRequired,
  selectedPrograms: PropTypes.array.isRequired,
};
